import { useEffect, useId, useRef, type CSSProperties } from 'react'
import type { AgentColor } from '@/contract'
import type { FranciscoMood } from '@/lib/agents'
import { cn } from '@/lib/utils'
import { FRANCISCO_BODY_PATH } from './body'
import { trackPointer } from './pointer'
import { useReducedMotion } from './useReducedMotion'
import './francisco.css'

/* Francisco, the agent character (ported from to_francisco.py).
   Francisco geometry (viewBox 190 140 400 530) is mapped into the robot box (0 0 200 230) by MAP.
   Follows the pointer (face, head and body) unless sleepy, told not to, or prefers-reduced-motion. */

const MAP = 'translate(13.2 0) scale(0.434) translate(-190 -140)'

type FranciscoProps = {
    color: AgentColor
    mood?: FranciscoMood
    /** CSS width (number = px); the height follows the 200:230 aspect. */
    size?: number | string
    /** Accessible name; without it the figure is decorative (aria-hidden). */
    label?: string
    /** Follow the pointer (default true). Paused agents stay still. */
    follow?: boolean
    /** "agent" (default): the robot head with a screen face. "brand": the original ChildFund Francisco, flat, round head, no face. */
    variant?: 'agent' | 'brand'
    className?: string
    style?: CSSProperties
}

const line = (d: string, width: number) => (
    <path d={d} style={{ fill: 'none', strokeWidth: width, strokeLinecap: 'round' }} />
)

export function Francisco({ color: c, mood = 'rest', size = 48, label, follow = true, variant = 'agent', className, style }: FranciscoProps) {
    const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const reduced = useReducedMotion()
    const svgRef = useRef<SVGSVGElement>(null)
    const bodyRef = useRef<SVGGElement>(null)
    const headRef = useRef<SVGGElement>(null)
    const faceRef = useRef<SVGGElement>(null)
    const following = follow && mood !== 'sleepy' && !reduced
    // Thinking: the face looks up and to the side even when still.
    const faceBase: [number, number] = mood === 'thinking' ? [6, -6] : [0, 0]
    const faceRest = faceBase[0] || faceBase[1] ? `translate(${faceBase[0]}px, ${faceBase[1]}px)` : 'none'

    useEffect(() => {
        const svg = svgRef.current
        if (!following || !svg) return
        const f = (n: number) => n.toFixed(2)
        const stop = trackPointer(svg, (dx, dy) => {
            if (faceRef.current) faceRef.current.style.transform = `translate(${f(dx * 9 + faceBase[0])}px, ${f(dy * 7 + faceBase[1])}px)`
            if (headRef.current) headRef.current.style.transform = `translate(${f(dx * 5)}px, ${f(dy * 3)}px) rotate(${f(dx * 7)}deg)`
            if (bodyRef.current) bodyRef.current.style.transform = `translate(${f(dx * 2)}px, 0px) rotate(${f(dx * 3)}deg)`
        })
        return () => {
            stop()
            if (faceRef.current) faceRef.current.style.transform = faceRest
            if (headRef.current) headRef.current.style.transform = 'none'
            if (bodyRef.current) bodyRef.current.style.transform = 'none'
        }
        // faceBase/faceRest derive from mood
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [following, mood])

    const id = (name: string) => `${name}-${uid}`
    const url = (name: string) => `url(#${id(name)})`
    const stop = (offset: string, token: string) => <stop offset={offset} style={{ stopColor: `var(${token})` }} />
    const eyesOpen = mood === 'rest' || mood === 'working' || mood === 'thinking'

    return (
        <svg
            ref={svgRef}
            className={cn('rb', `rb-${mood}`, className)}
            viewBox="0 0 200 230"
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            focusable="false"
            style={{ width: size, height: 'auto', display: 'block', flex: 'none', overflow: 'visible', ...style }}
        >
            <defs>
                <radialGradient id={id('rbsh')} cx="35%" cy="25%" r="85%">
                    {stop('0', `--rb${c}-l`)}{stop('.55', `--rb${c}`)}{stop('1', `--rb${c}-d`)}
                </radialGradient>
                <radialGradient id={id('rbbd')} gradientUnits="userSpaceOnUse" cx="340" cy="420" r="300">
                    {stop('0', `--rb${c}-l`)}{stop('.5', `--rb${c}`)}{stop('1', `--rb${c}-d`)}
                </radialGradient>
                <linearGradient id={id('rbsc')} x1="0" y1="0" x2="0" y2="1">
                    {stop('0', '--rb-screen-t')}{stop('1', '--rb-screen-b')}
                </linearGradient>
                <radialGradient id={id('rbhl')} cx="30%" cy="20%" r="45%">
                    <stop offset="0" style={{ stopColor: 'var(--rb-white)', stopOpacity: 0.8 }} />
                    <stop offset="1" style={{ stopColor: 'var(--rb-white)', stopOpacity: 0 }} />
                </radialGradient>
                <filter id={id('rbgl')} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.3" result="b" />
                    <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>
            <ellipse className="rb-shadow" cx="100" cy="224" rx="46" ry="6" style={{ fill: 'var(--rb-shadow)', opacity: 0.22 }} />
            <g className="rb-float">
                <g ref={bodyRef} className="rb-body" style={{ transform: 'none' }}>
                    <g transform={MAP}>
                        <path d={FRANCISCO_BODY_PATH} fill={variant === 'brand' ? `var(--rb${c})` : url('rbbd')} />
                    </g>
                </g>
                {variant === 'brand' ? (
                    <g ref={headRef} className="rb-head" style={{ transform: 'none' }}>
                        <g transform={MAP}>
                            <circle cx="388" cy="273" r="54" style={{ fill: `var(--rb${c})` }} />
                        </g>
                    </g>
                ) : (
                <g ref={headRef} className="rb-head" style={{ transform: 'none' }}>
                    <g className="rb-head-in">
                        <g transform={MAP}>
                            <rect x="306" y="234" width="18" height="48" rx="9" fill={url('rbsh')} />
                            <rect x="452" y="234" width="18" height="48" rx="9" fill={url('rbsh')} />
                            <circle cx="388" cy="262" r="76" fill={url('rbsh')} />
                            <ellipse cx="364" cy="222" rx="42" ry="24" fill={url('rbhl')} opacity=".6" />
                            <rect
                                x="330" y="230" width="116" height="80" rx="36" fill={url('rbsc')}
                                style={{ stroke: 'var(--rb-white)', strokeOpacity: 0.5, strokeWidth: 1.5 }}
                            />
                        </g>
                        <g
                            ref={faceRef}
                            className="rb-face"
                            filter={url('rbgl')}
                            style={{ transform: faceRest, fill: `var(--rb${c}-glow)`, stroke: `var(--rb${c}-glow)` }}
                        >
                            <g transform={MAP}>
                                <g className="rb-eyes">
                                    {eyesOpen && (
                                        <>
                                            <circle cx="366" cy="262" r="9" style={{ stroke: 'none' }} />
                                            <circle cx="410" cy="262" r="9" style={{ stroke: 'none' }} />
                                        </>
                                    )}
                                    {mood === 'success' && line('M356 266 Q366 252 376 266M400 266 Q410 252 420 266', 6)}
                                    {mood === 'sleepy' && (
                                        <>
                                            <rect x="355" y="261" width="22" height="6" rx="3" style={{ stroke: 'none' }} />
                                            <rect x="399" y="261" width="22" height="6" rx="3" style={{ stroke: 'none' }} />
                                        </>
                                    )}
                                    {mood === 'confused' && (
                                        <>
                                            <circle cx="366" cy="263" r="6" style={{ stroke: 'none' }} />
                                            <circle cx="410" cy="260" r="10" style={{ stroke: 'none' }} />
                                        </>
                                    )}
                                </g>
                                {(mood === 'rest' || mood === 'working' || mood === 'success') && line('M374 283 Q388 296 402 283', 6)}
                                {mood === 'sleepy' && line('M381 288 h14', 5)}
                                {mood === 'confused' && line('M372 288 q3.5 -5 7 0 t7 0 t7 0 t7 0', 4.5)}
                                {mood === 'thinking' && <circle cx="388" cy="288" r="5" style={{ fill: 'none', strokeWidth: 4 }} />}
                            </g>
                        </g>
                    </g>
                </g>
                )}
            </g>
        </svg>
    )
}
